import React, { forwardRef, useContext, useImperativeHandle, useCallback } from 'react';
import styles from './DynamicForms.styles.js';
import useDynamicFormsHook from './DynamicForms.hook.js';
import { Select, Table, Popover } from 'antd';
import { InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { AppContext } from 'App.context.js';
import useGuestHook from '../../PMS/PMSSelector/Guest/hooks/Guest.hook';
import UnavailableBadge from '../../CheckIn/GuestTable/UnavailableInPms/UnavailableInPms';
import { useSupportedFields } from '../../CheckIn/GuestTable/useSupportedFields.js';
import usePMSUDFConfigHook from '../../PMS/PMSUDFConfig/PMSUDFConfig.hook';
import stylesSection from './DynamicFormsHeader/DynamicFormsHeader.styles.js';
import DynamicFormsRowType from './DynamicFormsRowType';
import DynamicFormsRow from './DynamicFormsRow/DynamicFormsRow.jsx';
import Colors from 'constants/Colors.js';
import './style.css';
import { showInfo, showWarning } from '../../InfoPMS/InfoPMS';
import constants from '../../../../utils/constants.js';

const { Option } = Select;
const { HEADER, SECTION, FIELD, FIELD_GROUP } = DynamicFormsRowType;

const DynamicForms = forwardRef(function DynamicForms(
  {
    isLoading,
    form,
    onSave,
    selectedLanguage,
    languages,
    selectedFieldName,
    onFieldSelected,
    selectedHotel,
    isDynamicForm,
  },
  ref
) {
  const {
    appState,
    appActions: { translate, getSelectedChain },
  } = useContext(AppContext);
  const { snakeToCamel } = useGuestHook();
  const { state, actions } = useDynamicFormsHook(form, selectedLanguage, selectedFieldName);

  const saveClicked = useCallback(
    applyToAllHotels => {
      onSave(state.form, applyToAllHotels, selectedFieldName);
    },
    [onSave, state.form, selectedFieldName]
  );

  useImperativeHandle(ref, () => ({ saveClicked }), [saveClicked]);

  const { supportedFields, pmsModuleName } = useSupportedFields();
  const { state: pmsUdfState } = usePMSUDFConfigHook();

  const getFieldStatus = fieldName => {
    const fieldNameCamel = snakeToCamel(fieldName).trim();
    const isAvailable =
      supportedFields.some(f => f.civitfun.trim() === fieldNameCamel) ||
      pmsUdfState?.hotelUdfs?.some(udf => snakeToCamel(udf.validate) === fieldNameCamel);

    if (getSelectedChain()?.id == 386 && fieldName == 'documentCountry') return 'available';

    if (isAvailable) return 'available';
    if (['Cloudbeds', 'Cloudbeds_new', 'Ohip', 'Opera'].includes(pmsModuleName))
      return 'requiresMapping';
    return 'unavailable';
  };

  const renderFieldStatus = field => {
    switch (getFieldStatus(field)) {
      case 'available':
        return (
          <div style={{ ...styles.fieldStatus, color: '#319A81', backgroundColor: '#ECFDF3' }}>
            <div style={{ ...styles.iconStyle, backgroundColor: '#319A81' }} />
            {translate('guestTable.availableInPMS')}
          </div>
        );
      case 'requiresMapping':
        return (
          <div style={{ ...styles.fieldStatus, color: '#FF9800', backgroundColor: '#FFF3E0' }}>
            <div style={{ ...styles.iconStyle, backgroundColor: '#FF9800' }} />
            {translate('guestTable.requiresMapping')}
          </div>
        );
      case 'unavailable':
        return (
          <div style={{ ...styles.fieldStatus, color: '#D32F2F', backgroundColor: '#FDE8E8' }}>
            <div style={{ ...styles.iconStyle, backgroundColor: '#D32F2F' }} />
            {translate('guestTable.unavailableInPMS')}
          </div>
        );
      default:
        return null;
    }
  };

  const columns = [
    {
      title: translate('guestTable.fieldCol'),
      dataIndex: 'rowType',
      fixed: 'left',
      width: '14%',
      render: (rowType, item) => {
        switch (rowType) {
          case FIELD: {
            if (item.field === selectedFieldName) {
              return (
                <div style={styles.selectField}>
                  {actions.getFieldLiteral(item.literals)?.value}
                </div>
              );
            }
            const showInfoIcon = showInfo(pmsModuleName, item.field);
            const showWarningIcon = showWarning(item.field);
            const content = [];

            if (showInfoIcon) {
              const infoKey = `fieldInfoByPMS.${pmsModuleName}.${item.field}`;
              const infoText =
                translate(infoKey) === infoKey ? translate(item.field) : translate(infoKey);

              content.push(
                <Popover key="info" content={infoText} trigger="hover">
                  <InfoCircleOutlined style={{ marginRight: 9, padding: 4, fontSize: 14 }} />
                </Popover>
              );
            }

            if (showWarningIcon) {
              content.push(
                <Popover key="warning" content={translate(item.field)} trigger="hover">
                  <WarningOutlined
                    style={{
                      color: Colors.yellowButton,
                      marginRight: 8,
                      padding: 4,
                      fontSize: 14,
                    }}
                  />
                </Popover>
              );
            }

            if (item.field.includes('uploadDocument')) {
              return (
                <div>
                  {actions.getEditableInput({
                    item: actions.getFieldLiteral(item.literals),
                    onValueChanged: actions.onFieldTextChanged,
                    supportedLanguages: languages,
                    allLiterals: item.literals,
                    field: item.field,
                  })}
                </div>
              );
            } else {
              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    height: 54,
                    padding: 8,
                    backgroundColor: 'white',
                    maxWidth: '100%',
                  }}
                >
                  <span
                    title={actions.getFieldLiteral(item.literals)?.value}
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {actions.getFieldLiteral(item.literals)?.value}
                  </span>
                  <div style={{ marginLeft: 'auto', paddingLeft: '10px', display: 'flex' }}>
                    {content}
                  </div>
                </div>
              );
            }
          }
          case FIELD_GROUP:
          case SECTION:
            return <div style={{ ...styles.section }}>{translate(`guestTable.fieldCol`)}</div>;
          case HEADER:
            return {
              children: (
                <div style={{ ...stylesSection.wrapper, width: '100%' }}>
                  <div style={{ ...styles.selectFieldText }}>
                    {getSelectedChain()?.id === constants.CHAINS.MELIA ? (
                      <>
                        {translate('dynamicForms.selectField')}
                        <Select
                          value={selectedFieldName}
                          onChange={value => onFieldSelected(value)}
                          style={{ width: '380px' }}
                          optionLabelProp="label"
                        >
                          <Option
                            value="nationality"
                            label={translate('dynamicForms.fields.documentCountry')}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>{translate('dynamicForms.fields.documentCountry')}</span>
                              {renderFieldStatus('documentCountry')}
                            </div>
                          </Option>
                          <Option
                            value="country"
                            label={translate('dynamicForms.fields.residenceCountry')}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>{translate('dynamicForms.fields.residenceCountry')}</span>
                              {renderFieldStatus('country')}
                            </div>
                          </Option>
                          <Option
                            value="birthCountry"
                            label={translate('dynamicForms.fields.birthCountry')}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>{translate('dynamicForms.fields.birthCountry')}</span>
                              {renderFieldStatus('birthCountry')}
                            </div>
                          </Option>
                          <Option
                            value="documentIssuanceCountry"
                            label={translate('dynamicForms.fields.documentIssuanceCountry')}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>
                                {translate('dynamicForms.fields.documentIssuanceCountry')}
                              </span>
                              {renderFieldStatus('documentIssuanceCountry')}
                            </div>
                          </Option>
                          <Option
                            value="guest_nationality"
                            label={translate('dynamicForms.fields.nationality')}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>{translate('dynamicForms.fields.nationality')}</span>
                              {renderFieldStatus('nationality')}
                            </div>
                          </Option>
                        </Select>
                      </>
                    ) : (
                      <div style={{ height: '50px', visibility: 'hidden' }}></div>
                    )}
                  </div>

                  <div style={{ display: 'flex', width: '100%' }}>
                    <div
                      style={{
                        ...stylesSection.container,
                        width: '38%',
                        justifyContent: 'flex-start',
                      }}
                    >
                      {translate('guestTable.fieldCol')}
                    </div>
                    <div
                      style={{
                        ...stylesSection.container,
                        width: '62%',
                        justifyContent: 'flex-start',
                      }}
                    >
                      {translate('guestTable.fieldColPMS')}
                    </div>
                  </div>
                </div>
              ),
              props: { colSpan: 2 },
            };
          default:
            return null;
        }
      },
    },
    {
      title: translate('guestTable.fieldCol'),
      dataIndex: 'rowType',
      fixed: 'left',
      width: '22%',
      render: (rowType, item) => {
        switch (rowType) {
          case FIELD: {
            const fieldName = snakeToCamel(item.field);
            let pmsName = '';

            if (supportedFields.length == 0) return;

            supportedFields.forEach(supportedField => {
              if (fieldName.trim() === supportedField.civitfun.trim()) {
                pmsName = supportedField.pms;
              }
            });

            // check UDF values
            if (pmsUdfState) {
              pmsUdfState.hotelUdfs.forEach(udf => {
                if (snakeToCamel(udf.validate) == fieldName) {
                  if (pmsName) pmsName += ' + ' + udf.name;
                  else pmsName = udf.name;
                }
              });
            }

            // To have the same form like legacy
            if (
              pmsModuleName === 'Ohip' &&
              fieldName == 'nationality' &&
              getSelectedChain()?.id == 386
            ) {
              pmsName = 'country';
            }
            return (
              <div
                style={
                  item.field === selectedFieldName
                    ? { ...styles.selectField, borderLeft: undefined }
                    : {
                        display: 'flex',
                        alignItems: 'center',
                        height: 54,
                        padding: 8,
                        backgroundColor: 'white',
                      }
                }
              >
                {pmsName || (
                  <UnavailableBadge
                    pmsModuleName={pmsModuleName}
                    fieldName={fieldName}
                    isDynamicForm={isDynamicForm}
                  />
                )}
              </div>
            );
          }

          case FIELD_GROUP:
          case SECTION:
            return (
              <div
                style={{
                  ...styles.section,
                }}
              >
                {translate(`guestTable.fieldColPMS`)}
              </div>
            );
          case HEADER:
            return { props: { colSpan: 0 } };
          default:
            return null;
        }
      },
    },
    {
      dataIndex: 'rowType',
      render: (rowType, item) => {
        return (
          <div style={styles.valueContainer}>
            <DynamicFormsRow
              rowType={rowType}
              item={item}
              setFieldValue={actions.setFieldValue}
              pmsModuleName={pmsModuleName}
              pmsUdfState={pmsUdfState}
              supportedFields={supportedFields}
              selectedFieldName={selectedFieldName}
            />
          </div>
        );
      },
    },
  ].filter(Boolean);

  return (
    <div className={'dynamicForm'}>
      <Table
        showHeader={false}
        columns={columns}
        dataSource={state.form?.fields}
        isLoading={isLoading || state.isLoading}
        pagination={false}
        scroll={{ x: 10 }}
      />
    </div>
  );
});

export default DynamicForms;
